// Feature: Issues (including JS errors), releases, heatmaps, funnels,
// dashboards, and vitals.
// Registers routes and nav entries; see features/registry.ts.
import { createRoute } from "@tanstack/react-router";
import { registerFeature } from "../registry";
import { DashboardPage } from "./pages/DashboardPage";
import { TimeSpentPage } from "./pages/TimeSpentPage";
import { FunnelDetailPage, FunnelsPage } from "./pages/FunnelsPages";
import { HeatmapsPage } from "./pages/HeatmapsPage";
import { IssueDetailPage } from "./pages/IssueDetailPage";
import { IssuesPage } from "./pages/IssuesPage";
import { LegacyErrorDetailRedirect, LegacyErrorsRedirect } from "./pages/LegacyErrorRedirect";
import { ReleasesPage } from "./pages/ReleasesPage";
import { TrafficPage } from "./pages/TrafficPage";
import "./styles.css";

const passSearch = (s: Record<string, unknown>) => s;

/** Search an issue keeps when it was opened from a call, so Back can restore that call and the list filters around it. */
const CALL_RETURN_KEYS = ["range", "from", "to", "q", "relayed", "failedToConnect", "networkType", "minQuality", "maxQuality", "browser", "os", "device", "release", "endUser", "hasIssue"];

export function issueDetailSearch(s: Record<string, unknown>): { fromCall?: string } & Record<string, unknown> {
  const out: Record<string, unknown> = {};
  if (typeof s.fromCall === "string" && s.fromCall !== "") out.fromCall = s.fromCall;
  for (const k of CALL_RETURN_KEYS) {
    const v = s[k];
    if (typeof v === "string" || typeof v === "boolean" || typeof v === "number") out[k] = v;
  }
  return out;
}

registerFeature({
  routes: (appRoute) => [
    createRoute({ getParentRoute: () => appRoute, path: "/dashboard", component: DashboardPage, validateSearch: passSearch }),
    createRoute({ getParentRoute: () => appRoute, path: "/traffic", component: TrafficPage, validateSearch: passSearch }),
    createRoute({ getParentRoute: () => appRoute, path: "/issues", component: IssuesPage, validateSearch: passSearch }),
    createRoute({
      getParentRoute: () => appRoute,
      path: "/issues/$clusterId",
      component: IssueDetailPage,
      // `fromCall` is set when the issue was opened from a call, so Back returns
      // there instead of the issues list. The other keys are that call's list filters.
      validateSearch: issueDetailSearch,
    }),
    createRoute({ getParentRoute: () => appRoute, path: "/errors", component: LegacyErrorsRedirect, validateSearch: passSearch }),
    createRoute({ getParentRoute: () => appRoute, path: "/errors/$fingerprint", component: LegacyErrorDetailRedirect }),
    createRoute({ getParentRoute: () => appRoute, path: "/releases", component: ReleasesPage, validateSearch: passSearch }),
    createRoute({ getParentRoute: () => appRoute, path: "/heatmaps", component: HeatmapsPage, validateSearch: passSearch }),
    createRoute({ getParentRoute: () => appRoute, path: "/time-spent", component: TimeSpentPage, validateSearch: passSearch }),
    createRoute({ getParentRoute: () => appRoute, path: "/funnels", component: FunnelsPage }),
    createRoute({ getParentRoute: () => appRoute, path: "/funnels/$id", component: FunnelDetailPage, validateSearch: passSearch }),
  ],
  nav: [
    { label: "Dashboard", to: "/dashboard", section: "main", order: 10 },
    { label: "Traffic", to: "/traffic", section: "main", order: 12 },
    { label: "Issues", to: "/issues", section: "main", order: 20 },
    { label: "Releases", to: "/releases", section: "main", order: 30 },
    { label: "Heatmaps", to: "/heatmaps", section: "main", order: 40 },
    { label: "Time spent", to: "/time-spent", section: "main", order: 45 },
    { label: "Funnels", to: "/funnels", section: "main", order: 50, feature: "funnels", upgrade: "Team and above" },
  ],
});
